import React from 'react';
import { Checkbox, Form } from 'antd';
import {
  Banner,
  Col,
  DatePicker,
  Gap,
  Gutter,
  PercentInput,
  Row,
  Select,
  Text,
} from '@common/components';
import type {
  LoadBalancingScheduleShiftPreference,
  LoadBalancingShiftPatternPreference,
} from '@common/graphql';
import type { FullValidationErrorFragmentType } from '@common/graphql/fragments';
import { datetime } from '@common/services/datetime';
import {
  ISO_DATE,
  SHIFT_PATTERN_OPTIONS,
  SHIFT_TYPE_OPTIONS,
  buildRoleOptions,
  isEndBeforeStart,
  isPast,
  type WorkerProfileFormValues,
} from './_schedule-worker-profile-update-form-service';

const DATE_DISPLAY_FORMAT = 'MM/DD/YYYY';

// The common DatePicker speaks the datetime/dayjs wrapper; an empty stored value maps to null.
const toPickerDate = (iso: string) => (iso ? datetime(iso) : null);

// Presentational "Update Profile" form (WORK-6258). Pure: the host owns the data, the
// Save/Cancel footer, and the mutation (see useScheduleWorkerProfileUpdateForm). Mirrors the
// prototype layout; "Open to weekend shifts" is intentionally omitted (out of MVP scope).
export function ScheduleWorkerProfileUpdateForm({
  values,
  onChange,
  saveAttempted,
  serverErrors = [],
  datesDisabled = false,
  assignableRoles = [],
  chargeRole,
}: {
  values: WorkerProfileFormValues;
  onChange: (patch: Partial<WorkerProfileFormValues>) => void;
  saveAttempted: boolean;
  serverErrors?: FullValidationErrorFragmentType[];
  // When editing an existing change, both date fields are locked (WORK-6258).
  datesDisabled?: boolean;
  // The service line's role abbreviations — the Role picker's options.
  assignableRoles?: readonly string[];
  // The service line's configured charge-role abbreviation (data-driven). When the selected
  // role is the charge role, charge-eligibility is implied so the checkbox is hidden.
  chargeRole?: string;
}) {
  const roleOptions = buildRoleOptions(assignableRoles);
  const dateMissing = saveAttempted && !values.effectiveDate;
  const datePast =
    saveAttempted && !!values.effectiveDate && isPast(values.effectiveDate);
  const dateError = dateMissing
    ? 'Required'
    : datePast
      ? 'Cannot be in the past'
      : undefined;
  const endDateError =
    saveAttempted && isEndBeforeStart(values.effectiveDate, values.endDate)
      ? 'Cannot be before the effective date'
      : undefined;

  return (
    <Gap vertical size="md" align="stretch" fullWidth>
      <Text size="md" color="midnightRegular">
        Updates take effect on the selected date and remain active until the end
        date or a later update takes effect. Leave End Date blank to keep the
        update in effect.
      </Text>

      {serverErrors.length > 0 && (
        <Banner type="error" showIcon>
          <Gap vertical size="xxs" align="stretch">
            {serverErrors.flatMap((error) =>
              error.fullErrors.map((message) => (
                <Text size="sm" key={`${error.attribute}-${message}`}>
                  {message}
                </Text>
              ))
            )}
          </Gap>
        </Banner>
      )}

      <Form layout="vertical">
        <Row gutter={[12, 0]}>
          <Col span={12}>
            <Form.Item
              label="Effective Date"
              required
              validateStatus={dateError ? 'error' : undefined}
              help={dateError}
            >
              <DatePicker
                value={toPickerDate(values.effectiveDate)}
                onChange={(date) =>
                  onChange({ effectiveDate: date?.format(ISO_DATE) ?? '' })
                }
                format={DATE_DISPLAY_FORMAT}
                placeholder={DATE_DISPLAY_FORMAT}
                disabled={datesDisabled}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="End Date"
              validateStatus={endDateError ? 'error' : undefined}
              help={endDateError}
            >
              <DatePicker
                value={toPickerDate(values.endDate)}
                onChange={(date) =>
                  onChange({ endDate: date?.format(ISO_DATE) ?? '' })
                }
                format={DATE_DISPLAY_FORMAT}
                placeholder="Ongoing"
                disabled={datesDisabled}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Gutter direction="bottom" size="sm">
          <Text bold>Profile</Text>
        </Gutter>
        <Form.Item>
          <Checkbox
            checked={values.accountActive}
            onChange={(e) => onChange({ accountActive: e.target.checked })}
          >
            Account active
          </Checkbox>
        </Form.Item>
        <Row gutter={[12, 0]}>
          <Col span={12}>
            <Form.Item label="Role">
              <Select<string>
                value={values.role || undefined}
                onChange={(value) => onChange({ role: value })}
                options={roleOptions}
                placeholder="Select role"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Full-Time Equivalent (FTE) %">
              <PercentInput
                value={values.ftePercentage ?? undefined}
                onChange={(value) =>
                  onChange({
                    ftePercentage: value === null ? null : Number(value),
                  })
                }
                max={100}
                step={1}
              />
            </Form.Item>
          </Col>
        </Row>
        {values.role !== chargeRole && (
          <Form.Item>
            <Checkbox
              checked={values.chargeEligible}
              onChange={(e) => onChange({ chargeEligible: e.target.checked })}
            >
              Charge eligible
            </Checkbox>
          </Form.Item>
        )}

        <Gutter direction="bottom" size="sm">
          <Text bold>Preferences</Text>
        </Gutter>
        <Form.Item label="Shift type">
          <Select<LoadBalancingScheduleShiftPreference>
            value={values.preferredTime}
            onChange={(value) => onChange({ preferredTime: value })}
            options={SHIFT_TYPE_OPTIONS}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item label="Shift pattern">
          <Select<LoadBalancingShiftPatternPreference>
            value={values.shiftPatternPreference}
            onChange={(value) => onChange({ shiftPatternPreference: value })}
            options={SHIFT_PATTERN_OPTIONS}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Form>
    </Gap>
  );
}
